/**
 * HTTP Auth Interceptor
 * Automatically adds authentication tokens to HTTP requests and handles token refresh
 */

import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '@app/core/services/auth.service';

/**
 * Functional interceptor for Angular 20+ standalone approach
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  
  // Skip interceptor for auth endpoints
  if (isAuthEndpoint(req.url)) {
    return next(req);
  }

  // Check if token exists
  const token = authService.getAccessToken();
  if (!token) {
    return next(req);
  }

  // Check if token is expired or about to expire (within 5 minutes)
  if (isTokenExpired(token) || isTokenExpiringSoon(token, 5)) {
    // Token is expired or expiring soon, refresh it before making the request
    return handleTokenRefresh(req, next, authService);
  }

  // Token is valid, add it to request and proceed
  req = addToken(req, token);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized - token expired (backup in case server rejects)
      if (error.status === 401 && !isAuthEndpoint(req.url)) {
        return handle401Error(req, next, authService);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Token refresh state management
 */
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Proactively refresh token before making request
 */
function handleTokenRefresh(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshAccessToken().pipe(
      switchMap((tokens) => {
        isRefreshing = false;
        if (!tokens.accessToken) {
          // Refresh failed, logout user
          authService.logout().subscribe();
          return throwError(() => new Error('Token refresh failed'));
        }
        refreshTokenSubject.next(tokens.accessToken);
        return next(addToken(request, tokens.accessToken));
      }),
      catchError((error) => {
        isRefreshing = false;
        // Refresh failed, logout user
        authService.logout().subscribe();
        return throwError(() => error);
      })
    );
  } else {
    // Wait for ongoing token refresh to complete
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next(addToken(request, token!)))
    );
  }
}

/**
 * Handle 401 error by refreshing token
 */
function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<any>> {
  // Reuse the same refresh logic
  return handleTokenRefresh(request, next, authService);
}

/**
 * Add authorization token to request
 */
function addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Check if URL is an auth endpoint
 */
function isAuthEndpoint(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/password')
  );
}

/**
 * Parse JWT token payload
 */
function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if token is expired
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) {
      return true;
    }
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp;
  } catch {
    return true;
  }
}

/**
 * Check if token is expiring soon
 * @param token - JWT token
 * @param minutes - Number of minutes before expiration to consider as "expiring soon"
 */
function isTokenExpiringSoon(token: string, minutes: number = 5): boolean {
  try {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) {
      return true;
    }
    const exp = payload.exp * 1000; // Convert to milliseconds
    const bufferTime = minutes * 60 * 1000; // Convert minutes to milliseconds
    return Date.now() >= (exp - bufferTime);
  } catch {
    return true;
  }
}
